'use client';

import { type ReactNode, createContext, useContext, useState } from 'react';

interface Artist {
    slug: string;
}

export type PreviewMode = {
    mode: 'none' | 'live' | 'preview';
    code: string | null;
    token: string | null;
};

interface SiteContextType {
    isMenuOpen: boolean;
    isCalendarOpen: boolean;
    locale: string;
    siteId: string;
    previewMode: PreviewMode;
    artistPanelArtist: Artist | null;
    toggleMenu: () => void;
    toggleCalendar: () => void;
    closeAll: () => void;
    setArtistPanelArtist: (artist: Artist | null) => void;
}

const SiteContext = createContext<SiteContextType | undefined>(undefined);

export function SiteProvider({
    children,
    locale,
    siteId,
    previewMode = { mode: 'none', code: null, token: null },
}: {
    children: ReactNode;
    locale: string;
    siteId: string;
    previewMode?: PreviewMode;
}) {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const [artistPanelArtist, setArtistPanelArtist] = useState<Artist | null>(null);

    // No need to sync preview mode - creating fresh GraphQL instances per request

    const toggleMenu = () => {
        setIsMenuOpen((prev) => !prev);
        if (isCalendarOpen) setIsCalendarOpen(false);
    };

    const toggleCalendar = () => {
        setIsCalendarOpen((prev) => !prev);
        if (isMenuOpen) setIsMenuOpen(false);
    };

    const closeAll = () => {
        setIsMenuOpen(false);
        setIsCalendarOpen(false);
        setArtistPanelArtist(null);
    };

    const handleSetArtistPanelArtist = (artist: Artist | null) => {
        setArtistPanelArtist(artist);
    };

    return (
        <SiteContext.Provider
            value={{
                isMenuOpen,
                isCalendarOpen,
                locale,
                siteId,
                previewMode,
                artistPanelArtist,
                toggleMenu,
                toggleCalendar,
                closeAll,
                setArtistPanelArtist: handleSetArtistPanelArtist,
            }}
        >
            {children}
        </SiteContext.Provider>
    );
}

export function useSite() {
    const context = useContext(SiteContext);
    if (context === undefined) {
        throw new Error('useSite must be used within a SiteProvider');
    }
    return context;
}
